/* eslint-disable camelcase */
import { extractParamsFromUrl } from '../utils/saga';
import { call, put } from 'redux-saga/effects';
import { OK } from 'http-status-codes';
import { createReducer } from 'reduxsauce';
import { normalize } from 'normalizr';
import { combineReducers } from 'redux';

export const BASE_INITIAL_STATE = {
  fetching: false,
  fetched: false,
  data: {},
  error: null,
};

export const EXTEND_NORMALIZED_INITIAL_STATE = {
  ...BASE_INITIAL_STATE,
  items: {},
  result: {
    results: [],
  },
};

export const DETAILS_SET_INITIAL_STATE = {};

export const FETCH_INITIAL_STATE = {
  fetching: false,
  fetched: false,
  error: null,
};

export const BASE_ACTIONS_SET = {
  request: ['initiator'],
  success: ['data', 'initiator'],
  failure: ['error', 'initiator'],
};

export const BASE_DETAILS_ACTIONS_SET = {
  request: ['id', 'initiator'],
  success: ['id', 'data', 'initiator'],
  failure: ['id', 'error', 'initiator'],
};

export const PAGINATED_ACTIONS_SET = {
  ...BASE_ACTIONS_SET,
  successPaginated: ['data', 'initiator'],
};

export const FAVOURITE_ACTIONS_SET = {
  removeFavourite: ['id'],
  markAsFavourite: ['id'],
  toggleFavourite: ['id', 'isFavourite'],
};

/**
 * Fetch state reducer
 */
export const getFetchState = (
  requestType,
  successType,
  failureType,
  initialState = FETCH_INITIAL_STATE,
) => {
  return combineReducers({
    fetching: createReducer(initialState.fetching, {
      [requestType]: () => true,
      [successType]: () => false,
      [failureType]: () => false,
    }),
    fetched: createReducer(initialState.fetched, {
      [successType]: () => true,
    }),
    error: createReducer(initialState.error, {
      [requestType]: () => initialState.error,
      [failureType]: (_, { error }) => error,
    }),
  });
};

/**
 * Parse next page from `next` key, and set
 * as `nextPage` number.
 * @param {Object} data - list response data
 */
export const parseNextPageToResponse = data => {
  const PAGE_NUMBER_BASE = 10;

  const nextPage = data.next
    ? parseInt(extractParamsFromUrl(data.next).page, PAGE_NUMBER_BASE)
    : null;
  return {
    ...data,
    nextPage,
  };
};

export const getRequestHandler =
  (initialState = BASE_INITIAL_STATE) =>
  state => ({
    ...state,
    fetching: true,
    error: initialState.error,
  });

export const getFailureHandler =
  () =>
  (state, { error }) => ({
    ...state,
    fetching: false,
    error,
  });

/**
 * Generate configurable success list handler.
 * Support pagination flow - add another entities to existing one in `results`.
 * @param {Object} options - configuration for handler
 */
export const getSuccessListHandler =
  (options = { attach: false }) =>
  (state, { data }) => {
    const prevResults = (state.data && state.data.results) || [];

    return {
      ...state,
      fetching: false,
      fetched: true,
      error: null,
      data: {
        ...data,
        results: options.attach
          ? [...prevResults, ...((data && data.results) || [])]
          : (data && data.results) || [],
      },
    };
  };

/**
 * Simple helper that wraps "SUCCESS" scenario handlers with
 * parsing and formating response.data as normalized, and add
 * it as keys.
 *
 * Possibly: could be modified to standalone solution for success.
 * Will be priority solution after using "selectors" (reselect library, or manual).
 */
export const getExtendNormalized =
  (getHandler, entity, options = {}) =>
  (state, action) => {
    const { attach: attachOption } = options;
    const { data } = action;

    const responseScheme = { results: [entity] };
    const normalizedData = normalize(data, responseScheme);
    const normalizedItems = normalizedData.entities[entity.key] || {};
    const normalizedResult = normalizedData.result;

    if (attachOption) {
      return {
        ...getHandler(options)(state, action),
        items: { ...state.items, ...normalizedItems },
        result: {
          ...normalizedResult,
          results: Array.from(
            new Set(state.result.results.concat(normalizedResult.results)),
          ),
        },
      };
    }
    return {
      ...getHandler(options)(state, action),
      items: normalizedItems,
      result: normalizedResult,
    };
  };

export const getRequestDetailsHandler =
  initialState =>
  (state, { id }) => {
    const requestHandler = getRequestHandler(initialState);
    return {
      ...state,
      [id]: state[id]
        ? requestHandler(state[id])
        : requestHandler(initialState),
    };
  };

export const getSuccessDetailsHandler =
  () =>
  (state, { id, data }) => ({
    ...state,
    [id]: {
      ...state[id],
      fetching: false,
      fetched: true,
      data,
    },
  });

export const getFailureDetailsHandler =
  (failureHandler = getFailureHandler()) =>
  (state, { id, error }) => {
    return {
      ...state,
      [id]: failureHandler(state[id], { error }),
    };
  };

/**
 * FIX: both sagas below previously did `yield apiCall(...)` instead of
 * `yield call(apiCall, ...)`. Since `apiCall` is itself a generator
 * function (it ultimately wraps an axios/fetch call inside a saga-style
 * generator), invoking it directly (`apiCall(params)`) just creates a
 * suspended generator object — none of its body has run yet, no HTTP
 * request has been made, and `response` ends up being that inert
 * generator object instead of the eventual API result. That's why
 * `response.status` was always undefined and every paginated/detail fetch
 * fell into the "Unknown error from API" branch with nothing showing up in
 * the Network tab — no request was ever actually sent.
 *
 * `call(apiCall, ...args)` is the correct redux-saga effect for this: it
 * properly drives the generator (or plain async function) to completion
 * and yields its actual return value.
 *
 * Also removed the debug console.log/console.warn lines that were added
 * while diagnosing this — no longer needed now that the real fix is in.
 */
export const getPaginatedListSaga = (actions, apiCall) =>
  function* paginatedListSaga({ type: initiator, params, isEnabled }) {
    try {
      yield put(actions.request(initiator));

      const response = yield call(apiCall, params, isEnabled);

      if (response && response.status === OK) {
        const rawData = (response && response.data) || {};
        const processedData = parseNextPageToResponse(rawData);

        if (params && params.page) {
          yield put(actions.successPaginated(processedData, initiator));
        } else {
          yield put(actions.success(processedData, initiator));
        }
      } else {
        const detail =
          (response && response.data && response.data.detail) ||
          'Unknown error from API';
        throw new Error(detail);
      }
    } catch (error) {
      yield put(actions.failure(error.message || 'Unknown failure', initiator));
    }
  };

export const getDetailSaga = (actions, apiCall) =>
  function* detailSaga({ type: initiator, id }) {
    try {
      yield put(actions.request(id, initiator));

      const response = yield call(apiCall, id);

      if (response.status === OK) {
        yield put(actions.success(id, response.data, initiator));
      // getDetailSaga
      } else {
        throw Error(
          (response.data && response.data.detail) ||
            `Request failed (${response.status})`,
        );
      }
    } catch (error) {
      yield put(actions.failure(id, error.message, initiator));
    }
  };

// Remove element from normalized structure
// without clearing data from {key: data} store.
export const softRemoveHandler = (state, { id }) => {
  const stateToUpdate = { ...state };
  stateToUpdate.result.results = stateToUpdate.result.results.filter(
    resultId => resultId !== id,
  );
  return stateToUpdate;
};

export const toggleFavourite = (state, { id, isFavourite }) => ({
  ...state,
  items: {
    ...state.items,
    [id]: {
      ...(state && state.items[id]),
      is_save_coupon: isFavourite,
    },
  },
});

export const toggleFavouriteInDetail = (state, { id, isFavourite }) => {
  const new_state = {
    ...state,
    [id]: {
      ...state[id],
      data: {
        ...(state[id] && state[id].data),
        is_save_coupon: isFavourite,
      },
    },
  };
  return new_state;
};

export const toggleFavouriteInBusinessDetail = (state, { id, isFavourite }) => {
  const new_state = {
    ...state,
  };
  Object.keys(new_state).forEach(key => {
    const couponIndex =
      new_state[key].data.coupons &&
      new_state[key].data.coupons.findIndex(coupon => coupon.id === id);
    new_state[key].data.coupons[couponIndex].is_save_coupon = isFavourite;
  });

  return new_state;
};